import { Injectable } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { map, tap, withLatestFrom } from 'rxjs/operators';

import {
  getNotesFiltersFromLocalStorage,
  upsertNotesFilters,
  upsertNotesFiltersSuccess,
} from '@modules/notes/filters-store/notes-filters.actions';
import { selectActiveNotesFilters } from '@modules/notes/filters-store/notes-filters.selectors';
import { NotesFiltersInterface } from '@modules/notes/filters-store/notes-filters.service';
import { notesLoad } from '@modules/notes/store';
import { LocalStorageService } from '@shared/services/local-storage.service';
import { RouteParamsService } from '@shared/services/route-params.service';

import { NoteTypes } from 'mojo-irp-notes';

@Injectable()
export class NotesFiltersEffects {
  upsert$ = createEffect(() =>
    this.actions.pipe(
      ofType(upsertNotesFilters),
      withLatestFrom(this.store.select(selectActiveNotesFilters)),
      map(([{ notesFilters }, storedFilters]) => ({ ...storedFilters, ...notesFilters })),
      tap((notesFilters) => this.localStorage.setSavedState(notesFilters, notesFilters.id)),
      map((notesFilters) => upsertNotesFiltersSuccess({ notesFilters })),
    ),
  );

  upsertSuccess$ = createEffect(() =>
    this.actions.pipe(
      ofType(upsertNotesFiltersSuccess),
      map(() => notesLoad()),
    ),
  );

  getFromLocalStorage$ = createEffect(() =>
    this.actions.pipe(
      ofType(getNotesFiltersFromLocalStorage),
      map(
        ({
          defaultNoteFilter,
          route,
          isWidgetView,
        }: {
          defaultNoteFilter: NotesFiltersInterface;
          route: ActivatedRoute;
          isWidgetView: boolean;
        }) => {
          const searchPhrasesQueryParams = !!this.route.snapshot.queryParams.searchPhrases
            ? JSON.parse(this.route.snapshot.queryParams.searchPhrases)
            : [];

          let existingNoteFilter: NotesFiltersInterface | null = this.localStorage.getSavedState(
            defaultNoteFilter.id,
          );

          // Fix broken notes-filters in localStorage
          if (existingNoteFilter != null) {
            const subjectIdFromId = Number(existingNoteFilter.id.split(':')[1]);
            if (subjectIdFromId !== existingNoteFilter.subjectId) {
              existingNoteFilter.subjectId = subjectIdFromId;
            }
          }

          // when search phrases are saved in local storage but user try to navigate to the route without query params
          if (
            !!!searchPhrasesQueryParams.length &&
            !!existingNoteFilter?.searchPhrases.length &&
            !isWidgetView
          ) {
            this.routeParamsService.updateRouteParams(
              { searchPhrases: existingNoteFilter.searchPhrases },
              route,
            );
          }

          if (!existingNoteFilter) {
            return upsertNotesFilters({ notesFilters: defaultNoteFilter });
          }

          let notesTypeObject = {
            systemGenerated: true,
            userGenerated: true,
          };
          switch (
            !!this.route.snapshot.queryParams.notesType &&
            JSON.parse(this.route.snapshot.queryParams.notesType)
          ) {
            case NoteTypes.USER_GENERATED:
              notesTypeObject = { ...notesTypeObject, systemGenerated: false };
              break;
            case NoteTypes.SYSTEM_GENERATED:
              notesTypeObject = { ...notesTypeObject, userGenerated: false };
          }

          existingNoteFilter = {
            ...existingNoteFilter,
            ...notesTypeObject,
          };

          if (!!searchPhrasesQueryParams.length) {
            existingNoteFilter = {
              ...existingNoteFilter,
              searchPhrases: searchPhrasesQueryParams,
            };
          }

          return upsertNotesFilters({ notesFilters: existingNoteFilter });
        },
      ),
    ),
  );

  constructor(
    private localStorage: LocalStorageService,
    private actions: Actions,
    private store: Store<{}>,
    private route: ActivatedRoute,
    private routeParamsService: RouteParamsService,
  ) {}
}
